export function HowToUse() {
  return (
    <section className="howto card-section" id="how-to-use">
      <h2>How this page works</h2>
      <p className="howto-lede">
        Everything runs in your browser. Your images never leave this computer —
        no upload, no account, no server. Pick sizes, drop in your images,
        adjust marks and duplex mode, download the PDF, print, fold.
      </p>

      <ol className="howto-steps">
        <li>
          <h3>1. Pick your card and paper</h3>
          <p>
            Choose a card size (5×7, A2, Poker, Square 5×5, or Custom). For paper, anything
            bigger than the spread works at 100% scale — Letter or A4 are common but custom is
            fine too.
          </p>
        </li>
        <li>
          <h3>2. Drop in your images</h3>
          <p>
            Default is single-sided — just front and back. Switch to <strong>Two-sided</strong>
            {' '}to add inside-left and inside-right images, plus the inside spread and the
            duplex-related settings. JPG, PNG, or WebP. Each image is center-cropped to fit
            its panel at 300 DPI — bring your own crop if you need pixel-exact framing.
          </p>
        </li>
        <li>
          <h3>3. Check the preview</h3>
          <p>
            The preview canvases mirror what the PDF will look like — same layout, same marks,
            same image placement. If you're in Two-sided mode, you also get page 2.
          </p>
        </li>
        <li>
          <h3>4. Adjust the print settings</h3>
          <p>
            The print-settings section controls which guide marks the PDF gets, and (for
            two-sided cards) how the image layout adapts to your printer's duplex mode.
          </p>
        </li>
        <li>
          <h3>5. Download the PDF</h3>
          <p>
            Click <strong>Download printable PDF</strong>. The file you get has:
          </p>
          <ul className="howto-list">
            <li><strong>Page 1</strong> — the outside spread (back on the left, front on the right by default).</li>
            <li><strong>Page 2</strong> — the inside spread (only if you're in Two-sided mode and added any inside image). Mirrored for short-edge-flip duplex by default.</li>
            <li><strong>Final page</strong> — a brief printing reference you can hand to a print shop or keep next to your printer.</li>
          </ul>
        </li>
      </ol>

      <h2 id="marks">Guide marks — what each toggle does</h2>
      <p>
        All three marks are <strong>on by default</strong>. Each draws on both the
        PDF and the preview, so you see exactly what the printer will see.
      </p>
      <dl className="howto-rules">
        <dt>Corner L marks</dt>
        <dd>
          Eight short hairlines at the four corners of the spread, plus two ticks
          at the top and bottom of the fold line. Cut along these to trim the
          card to size.
        </dd>
        <dt>Fold line (center dashed)</dt>
        <dd>
          A dashed line down the middle of the spread, slightly overhanging top
          and bottom. Score and fold here.
        </dd>
        <dt>Card border (dashed)</dt>
        <dd>
          A dashed rectangle around the full unfolded card. Handy as a visual
          frame when you're trimming, or to show a print shop where the card
          edge is. Turn it off if you want a clean image-only output.
        </dd>
      </dl>

      <h2 id="duplex-mode">Duplex mode — short-edge vs long-edge vs two-sheet</h2>
      <p>
        Only relevant for two-sided cards. Home printers do two-sided printing
        one of two ways: by flipping the paper along its short edge, or along
        its long edge. Each puts your inside-page content in a different
        orientation, so the layout has to compensate. The "Duplex mode" buttons
        set the right compensation for you.
      </p>
      <dl className="howto-rules">
        <dt>Short-edge flip (default)</dt>
        <dd>
          The most common duplex mode for greeting cards. The printer flips the
          paper around a short edge, so the back is left-right mirrored. We
          compensate by <em>swapping</em> the inside-left and inside-right
          images on page 2 — so after the printer's flip, they land in the
          correct positions.
        </dd>
        <dt>Long-edge flip</dt>
        <dd>
          The printer flips along a long edge, so the back is top-bottom
          mirrored. We compensate by <em>rotating each inside panel 180°</em>
          (no left/right swap). Pick this if your printer's two-sided default
          is long-edge or "book" binding.
        </dd>
        <dt>Two-sheet (no flip)</dt>
        <dd>
          Treats the inside as its own one-sided print — no swap, no rotation.
          Print page 1 on one sheet and page 2 on another, then glue them
          back-to-back. Sidesteps duplex registration entirely; useful when
          your printer can't duplex cleanly.
        </dd>
      </dl>

      <h3>Advanced — manual flip/rotate overrides</h3>
      <p>
        Open the <strong>Advanced</strong> disclosure under the duplex buttons
        to see four individual toggles: swap left/right and rotate 180°, for
        each of the outside and inside spreads. The duplex-mode buttons are
        shortcuts that set these — but if your printer does something unusual
        (some inkjets are surprisingly creative), you can dial in the exact
        layout by hand. The preview re-renders live so you can confirm before
        downloading.
      </p>

      <h2 id="two-ways">Two ways to print this card</h2>
      <p>
        The same PDF works for either path. Print at home if you have a printer
        — it's fastest. If duplex is too messy or you want a sharper finish,
        drop the file at a local print shop.
      </p>

      <div className="howto-options howto-options-2">
        <article className="howto-option recommended">
          <span className="howto-label">Recommended</span>
          <h3>1. Print at home</h3>
          <p>
            Any home printer + cardstock. Two ways: print both sides on one
            sheet (<strong>duplex</strong>), or use the <strong>two-sheet</strong>
            mode in print settings and glue them back-to-back. Duplex is faster;
            two-sheet sidesteps printer drift entirely.
          </p>
          <p className="howto-pros">Fastest. No trip out.</p>
          <p className="howto-cons">Duplex may need one calibration print first.</p>
        </article>

        <article className="howto-option">
          <span className="howto-label">Best result, same-day</span>
          <h3>2. Print shop (walk-in)</h3>
          <p>
            Take the PDF to FedEx Office, Staples, The UPS Store, Office Depot,
            or a local copy shop. Professional duplex equipment registers
            tighter than any home printer. Tell them: <em>print at 100% /
            actual size on cardstock, duplex with short-edge flip, trim to
            outer crop marks.</em>
          </p>
          <p className="howto-pros">Sharpest result. Same-day pickup.</p>
          <p className="howto-cons">~$5–15 per card. Trip required.</p>
        </article>
      </div>

      <h2 id="print-settings">Print settings — the three rules</h2>
      <p>
        Same settings work whether you print at home or at a copy shop. The
        instructions page baked into your PDF says this too, so you don't have
        to flip back.
      </p>
      <dl className="howto-rules">
        <dt>Scale = Actual size / 100%</dt>
        <dd>
          Never "Fit to page". Fit-to-page shrinks the card so the crop marks
          land in the wrong place. The setting may be labeled "Actual size",
          "100%", or "None" — all equivalent.
        </dd>
        <dt>Orientation = Landscape</dt>
        <dd>
          The spread is wider than it is tall. Most printers auto-detect this;
          double-check if your print preview looks rotated.
        </dd>
        <dt>Sides = match the duplex mode you picked above</dt>
        <dd>
          For two-sided cards: if you chose short-edge flip in this app, set
          your printer's two-sided setting to "flip on short edge" (sometimes
          "landscape duplex"). If you chose long-edge, use "flip on long edge".
          For two-sheet, set the printer to single-sided and run each page on
          its own sheet.
        </dd>
      </dl>

      <h2 id="drift">If your duplex is off — drift calibration</h2>
      <p>
        Most home printers drift one to three millimetres between the front and
        back of a duplex print. Invisible on a memo, but enough to nudge a card
        layout off the fold line. The fix is one calibration print plus the
        drift offset on this page.
      </p>

      <ol className="howto-steps drift-steps">
        <li>
          <h3>1. Print the alignment-test PDF</h3>
          <p>
            Click the <strong>Print the alignment-test PDF first</strong> button in the
            Calibration section. Print it <em>duplex on plain paper, 100% scale</em>, using
            the same flip mode (short or long edge) you'll use for the card.
          </p>
        </li>
        <li>
          <h3>2. Measure the offset</h3>
          <p>
            Hold the printed sheet up to a bright window. The back-side grid
            will be faintly visible through the front. Read the offset of page
            2's center cross from page 1's, in millimetres, off the labeled
            rulers. Both axes are signed: <em>dx</em> positive = right,
            {' '}<em>dy</em> positive = down.
          </p>
        </li>
        <li>
          <h3>3. Enter dx and dy</h3>
          <p>
            Type the values in the Calibration section's dx/dy fields. The inside-spread
            preview updates live.
          </p>
        </li>
        <li>
          <h3>4. Re-download the PDF</h3>
          <p>
            Hit <strong>Download printable PDF</strong> again. The inside page
            is now pre-shifted by your <em>dx</em> / <em>dy</em>, so when your
            printer applies its same drift, the inside lands exactly where it
            should.
          </p>
        </li>
      </ol>

      <h3 id="drift-fallback">When calibration won't help</h3>
      <p>
        Drift over about 5 mm, or drift that changes from print to print, is
        more than the calibration can absorb cleanly. Two good fallbacks:
      </p>
      <ul className="howto-list">
        <li>
          <strong>Switch to two-sheet mode</strong> in print settings and glue the pages
          back-to-back. No duplex registration to fail.
        </li>
        <li>
          <strong>Use a walk-in print shop.</strong> Their duplex equipment is far tighter
          than a home unit.
        </li>
      </ul>

      <h2 id="paper-envelopes">Paper &amp; envelopes</h2>
      <p>
        You can print on whatever paper you already have — the card prints at
        its actual size and the surrounding margin can be any size, so larger
        paper just means more margin. Use the <strong>Custom</strong> paper
        option if your stock isn't Letter or A4.
      </p>
      <dl className="howto-rules">
        <dt>Paper</dt>
        <dd>
          Letter (8.5 × 11 in) is standard in the US, Canada, Mexico. A4
          (210 × 297 mm) is standard most other places. Cardstock 80–110 lb
          (200–300 gsm) feels best; plain paper works in a pinch.
        </dd>
        <dt>Envelopes for 5×7 cards</dt>
        <dd>A7 envelope (5.25 × 7.25 in).</dd>
        <dt>Envelopes for A2 (4¼ × 5½ in) cards</dt>
        <dd>A2 envelope (4⅜ × 5¾ in).</dd>
        <dt>Envelopes for Poker (2.5 × 3.5 in) cards</dt>
        <dd>
          Business-card sleeves, or trim down an A1 envelope. Most papercrafters
          use this size for tabletop prototyping rather than mailing.
        </dd>
        <dt>Envelopes for Square 5×5 cards</dt>
        <dd>5¼ × 5¼ in square envelopes. Check your postal service — some carriers surcharge non-rectangular mail.</dd>
      </dl>

      <h2 id="privacy">Privacy &amp; cost</h2>
      <p>
        This tool is free and stays free. The site is one HTML/JS file
        downloaded once and run locally — there's no backend, no analytics that
        identifies you, no account, no upload. You own the PDFs you generate
        and can use them for anything, including commercial work.
      </p>
    </section>
  );
}
